const PRODUCTION_PUBLIC_DOMAIN = "getonground.com";
const STAGING_PUBLIC_DOMAIN = "staging.getonground.com";

const RESERVED_SUBDOMAINS = new Set([
  "www",
  "admin",
  "api",
  "app",
  "assets",
  "auth",
  "cdn",
  "dashboard",
  "mail",
  "staging",
  "staging-admin",
  "staging-api",
  "static",
  "support",
]);

const VENUE_SUBDOMAIN_PATTERN = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

const normalizeHostname = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/\.$/, "")
    .replace(/:\d+$/, "");

export const normalizePublicWebsiteSubdomain = (value) =>
  String(value || "").trim().toLowerCase();

export const getPublicWebsiteSubdomainError = (value) => {
  const subdomain = normalizePublicWebsiteSubdomain(value);

  if (!subdomain) return "";
  if (subdomain.length < 3 || subdomain.length > 63) {
    return "Use between 3 and 63 characters.";
  }
  if (!VENUE_SUBDOMAIN_PATTERN.test(subdomain)) {
    return "Use lowercase letters, numbers, and internal hyphens only.";
  }
  if (RESERVED_SUBDOMAINS.has(subdomain)) {
    return "This subdomain is reserved.";
  }

  return "";
};

const normalizeVenueSlug = (value) => {
  const slug = String(value || "").trim();
  return slug && !/[/?#]/.test(slug) ? slug : "";
};

const isStagingHostname = (hostname) => {
  const host = normalizeHostname(hostname);

  return (
    host === STAGING_PUBLIC_DOMAIN ||
    host === "staging-admin.getonground.com" ||
    host === "staging-api.getonground.com" ||
    host.endsWith(`.${STAGING_PUBLIC_DOMAIN}`)
  );
};

const isProductionHostname = (hostname) => {
  const host = normalizeHostname(hostname);

  return (
    !isStagingHostname(host) &&
    (host === PRODUCTION_PUBLIC_DOMAIN ||
      host.endsWith(`.${PRODUCTION_PUBLIC_DOMAIN}`))
  );
};

export const resolveVenueSubdomainFromHostname = (hostname) => {
  const host = normalizeHostname(hostname);
  const stagingSuffix = `.${STAGING_PUBLIC_DOMAIN}`;
  const productionSuffix = `.${PRODUCTION_PUBLIC_DOMAIN}`;
  let candidate = "";

  if (host.endsWith(stagingSuffix)) {
    candidate = host.slice(0, -stagingSuffix.length);
  } else if (host.endsWith(productionSuffix)) {
    candidate = host.slice(0, -productionSuffix.length);
  }

  const subdomain = normalizePublicWebsiteSubdomain(candidate);

  return getPublicWebsiteSubdomainError(subdomain)
    ? ""
    : subdomain;
};

// Backward-compatible export for the first subdomain-routing commit.
export const resolveVenueSlugFromHostname =
  resolveVenueSubdomainFromHostname;

const normalizeUrlInput = (value) => {
  if (value && typeof value === "object") {
    return {
      subdomain: normalizePublicWebsiteSubdomain(value.subdomain),
      slug: normalizeVenueSlug(value.slug),
    };
  }

  const normalized = normalizePublicWebsiteSubdomain(value);
  return { subdomain: normalized, slug: normalizeVenueSlug(value) };
};

export const buildPublicVenueUrl = (
  value,
  locationLike = window.location,
) => {
  const { subdomain, slug } = normalizeUrlInput(value);
  const validSubdomain =
    subdomain && !getPublicWebsiteSubdomainError(subdomain)
      ? subdomain
      : "";
  const hostname = normalizeHostname(locationLike?.hostname);

  if (validSubdomain && isStagingHostname(hostname)) {
    return `https://${validSubdomain}.${STAGING_PUBLIC_DOMAIN}`;
  }

  if (validSubdomain && isProductionHostname(hostname)) {
    return `https://${validSubdomain}.${PRODUCTION_PUBLIC_DOMAIN}`;
  }

  if (!slug) return "";

  const publicOrigin = isStagingHostname(hostname)
    ? `https://${STAGING_PUBLIC_DOMAIN}`
    : isProductionHostname(hostname)
      ? `https://${PRODUCTION_PUBLIC_DOMAIN}`
      : String(locationLike?.origin || "").replace(/\/+$/, "");

  return publicOrigin
    ? `${publicOrigin}/venues/${encodeURIComponent(slug)}`
    : `/venues/${encodeURIComponent(slug)}`;
};
